'use client'

import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PhoneIcon, EnvelopeIcon, MapPinIcon, ClockIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { getBranchData, getOtherBranches, getCurrentBranch, getBranchCodeFromPath, regionalizePath, type BranchData } from '@/lib/config/branches-config'
import { usePathname, useRouter } from 'next/navigation'

interface RegionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBranchCode?: string
}

export default function RegionModal({ open, onOpenChange, currentBranchCode }: RegionModalProps) {
  const pathname = usePathname() || '/'
  const router = useRouter()
  
  // Определяем текущий регион из пути, если не передан явно
  const actualBranchCode = currentBranchCode || getBranchCodeFromPath(pathname)
  const currentBranch = getBranchData(actualBranchCode) || getCurrentBranch()
  const otherBranches = getOtherBranches(actualBranchCode)

  const handleBranchClick = (branch: BranchData) => {
    // Извлекаем текущий путь без регионального префикса
    // Удаляем региональный префикс из текущего пути
    let cleanPath = pathname
    
    // Удаляем префикс /geo/[code]/
    const geoMatch = pathname.match(/^(\/[^\/]+)?\/geo\/[^\/]+(.*)$/);
    if (geoMatch) {
      // geoMatch[1] - языковой префикс (если есть), geoMatch[2] - остаток пути
      const langPrefix = geoMatch[1] || '';
      const restPath = geoMatch[2] || '/';
      cleanPath = langPrefix + restPath;
    }
    
    // Если путь пустой или только корень, используем базовый URL региона
    if (!cleanPath || cleanPath === '/' || cleanPath === '') {
      cleanPath = branch.url || '/';
    }
    
    // Формируем новый путь с учетом выбранного региона
    const newPath = regionalizePath(cleanPath, branch.code)
    
    // Используем router для навигации
    router.push(newPath)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-bold">Выбор филиала</DialogTitle>
          </div>
        </DialogHeader>
        
        <div className="px-6 pb-6 space-y-6">
          {/* Текущий филиал */}
          <div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-lg font-semibold text-blue-900 mb-4">
              Сейчас выбран филиал - {currentBranch.name}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Левая колонка - Контакты */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <a 
                      href={`tel:${currentBranch.phone.replace(/[^+\d]/g, '')}`}
                      className="text-sm text-gray-700 hover:text-blue-600"
                    >
                      {currentBranch.phone}
                    </a>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <a 
                      href={`mailto:${currentBranch.email}`}
                      className="text-sm text-gray-700 hover:text-blue-600"
                    >
                      {currentBranch.email}
                    </a>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700">
                      {currentBranch.address}
                    </span>
                  </div>
                </div>
                
                {/* Правая колонка - Часы работы */}
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-700 space-y-1">
                      {currentBranch.workingHours.weekdays && (
                        <div>{currentBranch.workingHours.weekdays}</div>
                      )}
                      {currentBranch.workingHours.friday && (
                        <div>{currentBranch.workingHours.friday}</div>
                      )}
                      {currentBranch.workingHours.lunch && (
                        <div>{currentBranch.workingHours.lunch}</div>
                      )}
                      {currentBranch.workingHours.weekend && (
                        <div>{currentBranch.workingHours.weekend}</div>
                      )}
                      {currentBranch.shippingHours && (
                        <div className="mt-2 font-medium">{currentBranch.shippingHours}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Список других филиалов */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Выберите ближайший филиал
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {otherBranches.map((branch) => (
                <button
                  key={branch.code}
                  onClick={() => handleBranchClick(branch)}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors group w-full text-left"
                >
                  <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                    Филиал - {branch.name}
                  </span>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-600" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
